import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { getCartonPropertyGroupNamesForProductionGroup } from "@/api/carton-property-group-api";
import { getAvailableBatches, getSelectionPipeline } from "@/api/selection-api";
import { useCancellingQuery } from "@/hooks/use-cancelling-query";

interface UseAvailableBatchesProps {
	productionGroupId: string;
	pipelineId: string;
	machineGroupId: string | null;
	pageNumber: number;
	pageSize: number;
	filter: string | null;
}

const availableBatchPrefix = "availableBatches";
export const availableBatchesQueryKey = [availableBatchPrefix];

export const useAvailableBatches = ({
	productionGroupId,
	pipelineId,
	machineGroupId,
	pageNumber,
	pageSize,
	filter,
}: UseAvailableBatchesProps) => {
	const sortStepsQueryKey = ["selectionPipeline", pipelineId];
	const sortStepsQueryFn = async () => {
		const pipeline = await getSelectionPipeline(pipelineId);
		return pipeline.dataPipeline.sortSteps;
	};

	const { data: sortSteps, isLoading: sortStepsLoading } = useQuery({
		queryKey: sortStepsQueryKey,
		queryFn: sortStepsQueryFn,
		refetchInterval: false,
	});

	const cartonPropertyGroupsQueryKey = ["CartonPropertyGroups", productionGroupId];
	const cartonPropertyGroupsQueryFn = async () => {
		return await getCartonPropertyGroupNamesForProductionGroup(productionGroupId);
	};

	const { data: cartonPropertyGroups, isLoading: cartonPropertyGroupsLoading } = useQuery({
		queryKey: cartonPropertyGroupsQueryKey,
		queryFn: cartonPropertyGroupsQueryFn,
		refetchInterval: false,
	});

	const availableBatchesQueryFn = () =>
		getAvailableBatches({
			productionGroupId,
			machineGroupId,
			filter,
			pageNumber,
			pageSize,
			sortSteps: sortSteps || [],
			cartonPropertyGroups: cartonPropertyGroups || [],
		});

	const queryKey = [availableBatchPrefix, productionGroupId, machineGroupId, pageNumber, pageSize, filter];
	const {
		data: pageOfBatches,
		isLoading: pageOfBatchesLoading,
		refetch,
		error,
		dataUpdatedAt,
		isRefetching,
	} = useCancellingQuery({
		queryKey,
		queryFn: availableBatchesQueryFn,
		refetchInterval: 5000,
		enabled: Boolean(productionGroupId) && sortSteps !== undefined && cartonPropertyGroups !== undefined,
		placeholderData: keepPreviousData,
	});

	return {
		pageOfBatches,
		isLoading: pageOfBatchesLoading || sortStepsLoading || cartonPropertyGroupsLoading,
		refetch,
		error,
		dataUpdatedAt,
		isRefetching,
	};
};
